import { resolveLocale } from './locales';

const WESTERN_DIGIT = /[0-9]/g;
const ARABIC_INDIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

/** Standard Arabic abbreviation for Value Added Tax (VAT). */
export const ARABIC_VAT_TERM = 'ض.ق.م.';

export const usesArabicIndicDigits = (locale?: string | null): boolean =>
  resolveLocale(locale ?? undefined) === 'ar';

export const toArabicIndicDigits = (value: string | number): string =>
  String(value).replace(
    WESTERN_DIGIT,
    (digit) => ARABIC_INDIC_DIGITS[Number(digit)] ?? digit
  );

export const localizeDigitsForLocale = (
  value: string | number,
  locale?: string | null
): string =>
  usesArabicIndicDigits(locale) ? toArabicIndicDigits(value) : String(value);

export const formatDisplayInteger = (
  value: number | null | undefined,
  locale?: string | null
): string => localizeDigitsForLocale(value ?? 0, locale);

/** Ant Design Badge `count` prop — localizes numeric badges for Arabic. */
export const formatBadgeCount = (
  value: number | false | null | undefined,
  locale?: string | null
): string | number => {
  if (value === false || value == null) return 0;
  return formatDisplayInteger(value, locale);
};

/** BCP-47 locale tag with Eastern Arabic numerals for Intl.NumberFormat. */
export const normalizeNumberFormatLocale = (locale: string): string => {
  const language = locale.split('-u-')[0]?.split('_')[0]?.toLowerCase();
  if (language === 'ar') return 'ar-u-nu-arab';
  return locale.replace('_', '-');
};

export const replaceEnglishVatTerm = (text: string): string =>
  text.replace(/\bVAT\b/g, ARABIC_VAT_TERM);

export const TABLE_PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100] as const;

/** Ant Design table pagination page-size Select options. */
export const getLocalizedPageSizeSelectOptions = (
  t: (key: string) => string,
  locale?: string | null
) =>
  TABLE_PAGE_SIZE_OPTIONS.map((size) => ({
    value: size,
    label: `${formatDisplayInteger(size, locale)}${t('main.pagination.items-per-page')}`,
  }));

/** "Viewing 1-5 of 9 results" with locale-aware digits. */
export const formatPaginationSummaryText = (
  t: (key: string) => string,
  locale: string | null | undefined,
  range: [number, number],
  total: number
): string =>
  localizeDigitsForLocale(
    `${t('main.viewing')} ${formatDisplayInteger(range[0], locale)}-${formatDisplayInteger(
      range[1],
      locale
    )} ${t('main.of')} ${formatDisplayInteger(total, locale)} ${t('main.results')}`,
    locale
  );
